import { CheckOutlined, CloudUploadOutlined } from '@ant-design/icons';
import { Button, Checkbox, Empty, Modal, Skeleton, Upload } from 'antd';
import type { RcFile } from 'antd/es/upload';
import { useEffect, useMemo, useState } from 'react';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useNotify } from '@/hooks/useNotify';
import { useGetMediaImagesQuery, useUploadMediaImagesMutation } from '@/modules/media/mediaApi';
import type { MediaImage } from '@/types/media';
import { resolveMutationResult } from '@/utils/apiResponse';
import { getMediaImageUrl } from '@/utils/media';
import styles from './MediaPickerModal.module.scss';

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const MAX_SIZE_BYTES = 5 * 1024 * 1024;
const RECENT_MEDIA_LIMIT = 4;

interface MediaPickerModalProps {
  open: boolean;
  multiple?: boolean;
  value: string[];
  pinnedItems?: MediaImage[];
  onChange: (mediaIds: string[], items: MediaImage[]) => void;
  onClose: () => void;
}

export function MediaPickerModal({
  open,
  multiple = false,
  value,
  pinnedItems = [],
  onChange,
  onClose,
}: MediaPickerModalProps) {
  const notify = useNotify();
  const [draft, setDraft] = useState<string[]>(value);
  const [uploadedItems, setUploadedItems] = useState<MediaImage[]>([]);

  const { data, isLoading, isFetching } = useGetMediaImagesQuery(
    {
      page: 1,
      limit: RECENT_MEDIA_LIMIT,
      sortBy: 'createdAt',
      sortOrder: 'desc',
      status: 'active',
    },
    { skip: !open },
  );
  const [uploadMediaImages, { isLoading: isUploading }] = useUploadMediaImagesMutation();

  useGlobalLoader(isUploading);

  const recentImages = useMemo(() => data?.results ?? [], [data?.results]);
  const selectedSet = useMemo(() => new Set(draft), [draft]);

  const mergedImages = useMemo(() => {
    const merged: MediaImage[] = [];
    const seen = new Set<string>();

    const add = (item: MediaImage) => {
      if (seen.has(item.id)) return;
      seen.add(item.id);
      merged.push(item);
    };

    uploadedItems.forEach(add);
    pinnedItems.forEach(add);
    recentImages.forEach(add);

    return merged;
  }, [pinnedItems, recentImages, uploadedItems]);

  useEffect(() => {
    if (open) {
      setDraft(value);
      setUploadedItems([]);
    }
  }, [open, value]);

  const toggle = (id: string) => {
    if (multiple) {
      setDraft((current) =>
        current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
      );
      return;
    }

    setDraft([id]);
  };

  const uploadFiles = async (files: File[]) => {
    const validFiles = files.filter((file) => {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        notify.error(`${file.name}: please upload PNG, JPG, WEBP, or GIF.`);
        return false;
      }

      if (file.size > MAX_SIZE_BYTES) {
        notify.error(`${file.name}: image must be 5MB or smaller.`);
        return false;
      }

      return true;
    });

    if (!validFiles.length) return;

    const formData = new FormData();
    validFiles.forEach((file) => formData.append('images', file));

    const result = await resolveMutationResult<{ items: MediaImage[] }>(
      uploadMediaImages(formData),
    );

    if (!result.ok) {
      notify.error(result.message);
      return;
    }

    const items = result.data.items ?? [];
    notify.success(result.message ?? `${validFiles.length} image(s) uploaded.`);

    if (!items.length) return;

    setUploadedItems((current) => {
      const known = new Set(current.map((item) => item.id));
      return [...items.filter((item) => !known.has(item.id)), ...current];
    });

    const newIds = items.map((item) => item.id);
    setDraft((current) => {
      if (multiple) {
        return [...new Set([...current, ...newIds])];
      }
      return [newIds[0]];
    });
  };

  const handleBeforeUpload = (file: RcFile, fileList: RcFile[]) => {
    const isLastFile = file.uid === fileList[fileList.length - 1]?.uid;
    if (isLastFile) {
      void uploadFiles(fileList);
    }
    return false;
  };

  const handleApply = () => {
    const items = mergedImages.filter((item) => draft.includes(item.id));
    onChange(draft, items);
    onClose();
  };

  const uploadControl = (
    <Upload
      accept="image/png,image/jpeg,image/webp,image/gif"
      showUploadList={false}
      multiple={multiple}
      beforeUpload={handleBeforeUpload}
      disabled={isUploading}
    >
      <Button icon={<CloudUploadOutlined />} loading={isUploading}>
        Upload image{multiple ? 's' : ''}
      </Button>
    </Upload>
  );

  return (
    <Modal
      title="Pick from Media Manager"
      open={open}
      onCancel={onClose}
      width={880}
      footer={
        <div className={styles.footer}>
          <span className={styles.count}>{draft.length} selected</span>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={handleApply} disabled={!draft.length}>
            Apply
          </Button>
        </div>
      }
      destroyOnHidden
    >
      <div className={styles.toolbar}>
        {uploadControl}
        <span className={styles.hint}>
          Showing {RECENT_MEDIA_LIMIT} most recent uploads · PNG, JPG, WEBP, or GIF up to 5MB
        </span>
      </div>

      {isLoading || isFetching ? (
        <div className={styles.grid}>
          <Upload
            accept="image/png,image/jpeg,image/webp,image/gif"
            showUploadList={false}
            multiple={multiple}
            beforeUpload={handleBeforeUpload}
            disabled={isUploading}
            className={styles.uploadTileWrap}
          >
            <button type="button" className={styles.uploadTile} disabled={isUploading}>
              <CloudUploadOutlined />
              <span>Upload image</span>
            </button>
          </Upload>
          {Array.from({ length: RECENT_MEDIA_LIMIT }).map((_, index) => (
            <Skeleton.Image key={index} active className={styles.skeleton} />
          ))}
        </div>
      ) : mergedImages.length === 0 ? (
        <Empty description="No active images yet" className={styles.empty}>
          {uploadControl}
        </Empty>
      ) : (
        <div className={styles.grid}>
          <Upload
            accept="image/png,image/jpeg,image/webp,image/gif"
            showUploadList={false}
            multiple={multiple}
            beforeUpload={handleBeforeUpload}
            disabled={isUploading}
            className={styles.uploadTileWrap}
          >
            <button type="button" className={styles.uploadTile} disabled={isUploading}>
              <CloudUploadOutlined />
              <span>Upload image</span>
            </button>
          </Upload>

          {mergedImages.map((item) => {
            const src = getMediaImageUrl(item.imageLarge ?? item.imageSmall);
            const selected = selectedSet.has(item.id);

            return (
              <button
                key={item.id}
                type="button"
                className={[styles.item, selected ? styles.itemSelected : '']
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => toggle(item.id)}
              >
                {src ? <img src={src} alt="" className={styles.image} /> : null}
                <span className={styles.check}>
                  {selected ? <CheckOutlined /> : <Checkbox checked={false} />}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
